import { inject, Injectable, OnDestroy, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Timestamp } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, from, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { Album } from '../../models/album.model';
import { AppExceptionModel } from '../../models/app-exception.model';
import { UserModel } from '../../models/user.model';
import { AlbumService } from '../../services/album.service';
import { AuthService } from '../../services/auth.service';
import { ConnectionService } from '../../services/connection.service';
import * as albumActions from '../actions/album.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';

@Injectable()
export class AlbumEffects implements OnInit, OnDestroy {
  actions$ = inject(Actions);
  private connectionService = inject(ConnectionService);
  store: Store<AppState> = inject(Store<AppState>);
  private albumService = inject(AlbumService);
  private authService = inject(AuthService);
  toastr: ToastrService = inject(ToastrService);
  afs: AngularFirestore = inject(AngularFirestore);
  router: Router = inject(Router);

  user: UserModel | null | undefined;

  private logout$ = new Subject<void>();

  constructor() {}

  ngOnInit(): void {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.logout$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnDestroy(): void {
    this.logout$.next();
    this.logout$.complete();
  }

  getAlbums$ = createEffect(() =>
    this.actions$.pipe(
      ofType(albumActions.getAlbums),
      debounceTime(500),
      switchMap(({ groupId, lastAlbumDate, limit }) =>
        this.albumService
          .getAlbums({
            groupId,
            lastAlbumDate,
            limitRecords: limit ?? 10,
          })
          .pipe(
            map((albums: Album[]) => {
              return albumActions.addManyToStore({ albums: albums });
            }),
            catchError((error) => {
              console.error(error);
              return of(albumActions.getAlbumsFailure({ error }));
            }),
          ),
      ),
    ),
  );

  // Effect to Create Album
  createAlbum$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(albumActions.createAlbum),
      withLatestFrom(this.store.select((state) => state.user.user)),
      mergeMap(([{ album }, user]) => {
        if (!user) {
          return of(
            albumActions.createFailure({
              error: {
                code: 'ERR_USER_NOT_FOUND',
                title: 'User Not Found',
                message:
                  'Could not find the logged in user. Please login again.',
              } as AppExceptionModel,
            }),
          );
        }

        return this.connectionService.isConnected$.pipe(
          filter((isConnected) => isConnected),
          take(1),
          switchMap(() =>
            from(this.albumService.create(album)).pipe(
              map((a) => {
                return albumActions.createSuccess({ album: a });
              }),
              catchError((error) => {
                return of(albumActions.createFailure({ error }));
              }),
            ),
          ),
        );
      }),
    ),
  );

  // Effect to Update Album
  updateAlbum$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(albumActions.updateAlbum),
      withLatestFrom(this.store.select((state) => state.user.user)),
      switchMap(([{ id, album }, user]) =>
        combineLatest([
          this.store.select((state) => state.user.user).pipe(take(1)),
          this.connectionService.isConnected$.pipe(take(1)),
        ]).pipe(
          switchMap(([user, isConnected]) => {
            if (!isConnected) {
              return of(
                albumActions.updateFailure({
                  error: {
                    code: 'ERR_OFFLINE',
                    title: 'Offline',
                    message:
                      'You are offline. Please check your connection and try again.',
                  } as AppExceptionModel,
                }),
              );
            }

            if (!user) {
              return of(
                albumActions.updateFailure({
                  error: {
                    code: 'ERR_USER_NOT_FOUND',
                    title: 'User Not Found',
                    message:
                      'Could not find the logged in user. Please login again.',
                  } as AppExceptionModel,
                }),
              );
            }

            const tempAlbum = {
              ...album,
              modified: Timestamp.now(),
            };

            return from(this.albumService.update(id, tempAlbum)).pipe(
              map((a) => {
                return albumActions.updateSuccess({ album: a });
              }),
              catchError((error) => {
                return of(albumActions.updateFailure({ error }));
              }),
            );
          }),
        ),
      ),
    ),
  );

  clearAlbums$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(albumActions.clear());
      }),
    ),
  );
}
